import { normalizeErrorString } from '@medplum/core'
import type {
  ExtractResource,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { normalizeQuery, type QueryTypes } from '@seen/core/search'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import {
  ifMatchHeader,
  isVersionConflictError,
  ResourceVersionConflictError,
} from '#src/hooks/mutationConflicts.ts'
import { reportPutConflict } from '#src/hooks/mutationObservability.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface UseFhirUpsertMutationArgs<T extends ResourceType> {
  resource: ExtractResource<T>
  query: QueryTypes
}

export interface UseFhirUpsertMutationOptions<T extends ResourceType> {
  mutation?: Omit<
    UseMutationOptions<
      ExtractResource<T>,
      unknown,
      UseFhirUpsertMutationArgs<T>
    >,
    'mutationFn'
  >
  /**
   * Invoked when an upsert fails because the target resource was modified
   * between our read and write (HTTP 412). Fires only when the upsert
   * payload carries an explicit id + versionId — the "update an existing
   * resource" case. The "create if none exists" case has no versionId and
   * cannot conflict.
   */
  onVersionConflict?: (latest: Resource) => void
}

export function useFhirUpsertMutation<T extends ResourceType>(
  type: T,
  options?: UseFhirUpsertMutationOptions<T> | null
): UseMutationResult<
  ExtractResource<T>,
  unknown,
  UseFhirUpsertMutationArgs<T>
> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()
  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'ID is required')
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(cacheKeys.read(type, data.id), data)
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      if (error instanceof ResourceVersionConflictError) {
        toast.error(
          'This record was updated by someone else. Please review the latest version and try again.'
        )
      } else {
        toast.error(normalizeErrorString(error))
      }
      options?.mutation?.onError?.(error, variables, context)
    },
    mutationFn: async (args) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirUpsertMutation',
          type
        )
      }
      const parameters = normalizeQuery(args.query)
      const fromVersionId = args.resource.meta?.versionId
      const headers = ifMatchHeader(fromVersionId)

      try {
        return await medplum.upsertResource<ExtractResource<T>>(
          args.resource,
          parameters,
          headers ? { headers } : undefined
        )
      } catch (error) {
        if (!isVersionConflictError(error) || !args.resource.id) {
          throw error
        }

        const latest = (await medplum.readResource(
          type,
          args.resource.id
        )) as ExtractResource<T>

        queryClient.setQueryData(cacheKeys.read(type, args.resource.id), latest)

        reportPutConflict({
          resourceType: type,
          id: args.resource.id,
          fromVersionId,
          toVersionId: latest.meta?.versionId,
        })

        options?.onVersionConflict?.(latest)

        throw new ResourceVersionConflictError({
          fhirResourceType: type,
          resourceId: args.resource.id,
          fromVersionId,
          latest,
          cause: error,
        })
      }
    },
  })
}
